import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, Legend } from "recharts";
import { fetchJson, formatDateTime, formatNumber, type CampaignReport } from "@/lib/comms";
import { ErrorState, StatusBadge, TableSkeleton } from "./shared";

function Tile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-md border p-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-xl font-bold">{value}</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

export default function CampaignReportPanel({ campaignId }: { campaignId: string }) {
  const [status, setStatus] = useState("all");
  const { data, isLoading, error } = useQuery<CampaignReport>({
    queryKey: ["/api/admin/comms/campaigns", campaignId, "report", status],
    queryFn: () => fetchJson(`/api/admin/comms/campaigns/${campaignId}/report?status=${status}`),
    refetchInterval: 30_000,
  });
  if (error) return <ErrorState error={error} />;
  if (isLoading || !data) return <TableSkeleton rows={5} cols={4} />;
  const s = data.stats;
  return (
    <div className="space-y-5" data-testid="campaign-report">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <Tile label="Sent" value={formatNumber(s.sent)} sub={`${formatNumber(s.queued)} still queued`} />
        <Tile label="Delivered" value={`${data.rates.delivered}%`} sub={formatNumber(s.delivered)} />
        <Tile label="Opened" value={`${data.rates.opened}%`} sub={formatNumber(s.opened)} />
        <Tile label="Clicked" value={`${data.rates.clicked}%`} sub={`${formatNumber(s.clicked)} · ${data.rates.clickToOpen}% of opens`} />
        <Tile label="Bounced" value={`${data.rates.bounced}%`} sub={`${formatNumber(s.bounced)} · ${formatNumber(s.complained)} complaints`} />
        <Tile label="Unsubscribed" value={`${data.rates.unsubscribed}%`} sub={`${formatNumber(s.failed)} failed · ${formatNumber(s.sandboxed)} sandboxed`} />
      </div>

      {data.timeline.length > 1 && (
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data.timeline.map(t => ({ name: new Date(t.hour).toLocaleString("en-JM", { month: "short", day: "numeric", hour: "numeric", timeZone: "America/Jamaica" }), Sent: t.sent, Opened: t.opened, Clicked: t.clicked }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <ChartTooltip />
              <Legend />
              <Bar dataKey="Sent" fill="hsl(var(--primary))" />
              <Line dataKey="Opened" stroke="#16a34a" dot={false} />
              <Line dataKey="Clicked" stroke="#f59e0b" dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Links clicked</h4>
          {data.links.length ? (
            <Table>
              <TableHeader><TableRow><TableHead>URL</TableHead><TableHead className="text-right">Clicks</TableHead><TableHead className="text-right">People</TableHead></TableRow></TableHeader>
              <TableBody>{data.links.map(l => <TableRow key={l.url}><TableCell className="max-w-[320px] truncate text-xs" title={l.url}>{l.url}</TableCell><TableCell className="text-right">{l.clicks}</TableCell><TableCell className="text-right">{l.uniqueClicks}</TableCell></TableRow>)}</TableBody>
            </Table>
          ) : <p className="text-sm text-muted-foreground">No clicks yet.</p>}
        </div>
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">By email domain</h4>
          {data.domains.length ? (
            <Table>
              <TableHeader><TableRow><TableHead>Domain</TableHead><TableHead className="text-right">Sent</TableHead><TableHead className="text-right">Opened</TableHead><TableHead className="text-right">Bounced</TableHead></TableRow></TableHeader>
              <TableBody>{data.domains.map(d => <TableRow key={d.domain}><TableCell className="text-xs">{d.domain}</TableCell><TableCell className="text-right">{d.sent}</TableCell><TableCell className="text-right">{d.opened}</TableCell><TableCell className={`text-right ${d.bounced ? "text-destructive" : ""}`}>{d.bounced}</TableCell></TableRow>)}</TableBody>
            </Table>
          ) : <p className="text-sm text-muted-foreground">Nothing sent yet.</p>}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recipients</h4>
          <Select value={status} onValueChange={setStatus}><SelectTrigger className="h-8 w-40"><SelectValue /></SelectTrigger><SelectContent>{["all", "queued", "sent", "delivered", "bounced", "failed", "suppressed", "sandboxed"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select>
        </div>
        {data.sends.length ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Recipient</TableHead><TableHead>Status</TableHead><TableHead>Sent</TableHead><TableHead>Opened</TableHead><TableHead>Clicked</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.sends.map(x => (
                  <TableRow key={x.id}>
                    <TableCell><div className="text-sm">{[x.first_name, x.last_name].filter(Boolean).join(" ") || "–"} <Badge variant="outline" className="text-[10px]">{x.kind || "?"}</Badge></div><div className="text-xs text-muted-foreground break-all">{x.address}</div>{x.error && <div className="text-xs text-destructive">{x.error}</div>}</TableCell>
                    <TableCell><StatusBadge status={x.status} /></TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{formatDateTime(x.sent_at)}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{formatDateTime(x.opened_at)}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{formatDateTime(x.clicked_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : <p className="text-sm text-muted-foreground">No recipients with that status.</p>}
      </div>
    </div>
  );
}
